import dayjs from "dayjs";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { formatMoney, shortLabel, displayedAmount } from "@/lib/format";
import { STATUS_LIST, stageMeta } from "@/lib/domain-constants";
import { MinorBadge } from "@/components/contractings/pills";
import { nextDeadline } from "@/components/team/BoardStats";
import type { ContractingItem, TeamMember } from "@/types/domain";

type Severity = "high" | "medium" | "low";

export type Alert = {
  id: string;
  severity: Severity;
  title: string;
  detail: string;
  action?: { label: string; onClick: () => void; tone?: "primary" | "ghost" };
};

const DOT: Record<Severity, string> = {
  high: "bg-red-500",
  medium: "bg-amber-500",
  low: "bg-emerald-500",
};

/** Nombre corto de la licitación: el aviso es una frase, no un párrafo. */
/**
 * El monto en texto corrido. Acá no cabe la pastilla de las tarjetas, así que el
 * origen se dice con palabras. `null` cuando no hay ninguno de los dos.
 */
function amountLabel(item: ContractingItem): string | null {
  const { amount, fromBases } = displayedAmount(item);
  if (amount === null) return null;
  return `${formatMoney(amount)}${fromBases ? " (según bases)" : ""}`;
}

function label(item: ContractingItem) {
  // Bloqueada: descripción y nomenclatura vienen las dos en null, y el aviso
  // igual tiene que decir algo. Se puede guardar con la estrella sin desbloquear,
  // así que el tablero sí recibe bloqueadas.
  return shortLabel(
    item.contracting_description ??
      item.contracting_nomenclature ??
      "Oportunidad bloqueada",
    44,
  );
}

/** Las etapas en las que el SEACE ya resolvió: el CRM no debería seguir en blanco. */
const RESOLVED_STAGES = new Set([
  "ADJUDICADO",
  "CONSENTIDO",
  "CONTRATADO",
  "DESIERTO",
  "NULO",
  "CANCELADO",
]);

function stageKey(item: ContractingItem) {
  return (item.contractingversion_currentstagenormalized ?? "").toUpperCase();
}

/** Días desde el último movimiento del seguimiento, o null si nunca se tocó. */
function idleDays(item: ContractingItem): number | null {
  const at = item.contractinguser?.contractinguser_updatedat;
  return at ? dayjs().diff(dayjs(at), "day") : null;
}

function idleLabel(days: number | null) {
  if (days === null) return null;
  return days === 0 ? "movida hoy" : `${days} d sin movimiento`;
}

/** La segunda línea del aviso: los datos que hay, separados por punto medio. */
function meta(parts: (string | null | undefined)[]) {
  return parts.filter(Boolean).join(" · ");
}

/**
 * Reglas de "esto necesita movimiento hoy". Se calculan sobre lo que ya está
 * cargado: no hay endpoint de avisos, son lecturas del propio pipeline.
 */
export function buildTeamAlerts(
  items: ContractingItem[],
  members: TeamMember[],
  onOpen: (id: number) => void,
): Alert[] {
  const alerts: Alert[] = [];
  const nameOf = (userId?: string | null) =>
    members.find((member) => member.userId === userId)?.name ?? null;

  for (const item of items) {
    const tracking = item.contractinguser;
    const status = tracking?.contractinguser_status ?? 0;
    if (status >= 3) continue;

    const idle = idleDays(item);
    const place = item.district?.district_name ?? item.province?.province_name;
    const stage = stageMeta(item.contractingversion_currentstagenormalized).label;

    // 1. Sin dueño no se trabaja. Es el aviso más caro del tablero.
    if (!tracking?.assignedUserId) {
      alerts.push({
        id: `unassigned-${item.contracting_id}`,
        severity: "high",
        title: `${label(item)} sigue sin responsable`,
        detail: meta([stage, place, idleLabel(idle)]),
        action: {
          label: "Asignar",
          onClick: () => onOpen(item.contracting_id),
          tone: "primary",
        },
      });
      continue;
    }

    // 2. Un plazo encima manda sobre todo lo demás.
    const deadline = nextDeadline(item);
    if (deadline !== null && deadline.days <= 7) {
      alerts.push({
        id: `closing-${item.contracting_id}`,
        severity: deadline.days <= 3 ? "high" : "medium",
        title: `${label(item)} tiene un plazo en ${deadline.days} día${deadline.days === 1 ? "" : "s"}`,
        detail: meta([
          nameOf(tracking.assignedUserId),
          deadline.etapa,
          STATUS_LIST.find((s) => s.value === status)?.label,
        ]),
        action: {
          label: "Abrir",
          onClick: () => onOpen(item.contracting_id),
          tone: "primary",
        },
      });
      continue;
    }

    // 3. El SEACE ya avanzó y el CRM se quedó atrás: la etapa dice una cosa y el
    // estado comercial otra.
    if (status === 0 && RESOLVED_STAGES.has(stageKey(item))) {
      alerts.push({
        id: `stale-status-${item.contracting_id}`,
        severity: "medium",
        title: `${label(item)} ya está ${stage.toLowerCase()} pero sigue en “Sin estado”`,
        detail: meta([
          nameOf(tracking.assignedUserId),
          amountLabel(item),
          idleLabel(idle),
        ]),
        action: {
          label: "Actualizar",
          onClick: () => onOpen(item.contracting_id),
        },
      });
      continue;
    }

    // 4. Asignada hace rato y sin una sola nota: nadie sabe en qué quedó.
    if ((tracking.noteCount ?? 0) === 0 && idle !== null && idle >= 5) {
      const owner = nameOf(tracking.assignedUserId);
      alerts.push({
        id: `silent-${item.contracting_id}`,
        severity: "low",
        title: owner
          ? `${owner} no registra notas desde que se le asignó ${label(item)}`
          : `${label(item)} no tiene ni una nota`,
        detail: meta([
          STATUS_LIST.find((s) => s.value === status)?.label,
          `asignada hace ${idle} d`,
          "0 notas",
        ]),
        action: {
          label: "Recordar",
          onClick: () => onOpen(item.contracting_id),
        },
      });
      continue;
    }

    // 5. Nadie la ha mirado todavía.
    if (status === 0) {
      alerts.push({
        id: `nostatus-${item.contracting_id}`,
        severity: "medium",
        title: `${label(item)} está asignada pero sigue en “Sin estado”`,
        detail: meta([nameOf(tracking.assignedUserId), stage, idleLabel(idle)]),
        action: {
          label: "Actualizar",
          onClick: () => onOpen(item.contracting_id),
        },
      });
    }
  }

  const order: Record<Severity, number> = { high: 0, medium: 1, low: 2 };
  return alerts.sort((a, b) => order[a.severity] - order[b.severity]).slice(0, 8);
}

export function AttentionPanel({
  title,
  alerts,
  footnote,
}: {
  title: string;
  alerts: Alert[];
  footnote?: string;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border bg-card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-base font-bold">{title}</h3>
        <span className="text-xs text-slate-400">
          {alerts.length} aviso{alerts.length === 1 ? "" : "s"}
          {footnote ? ` · ${footnote}` : ""}
        </span>
      </div>

      {alerts.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Nada pendiente. Todo el pipeline tiene responsable y movimiento.
        </p>
      ) : (
        <ul className="flex flex-col gap-3.5">
          {alerts.map((alert) => (
            <li key={alert.id} className="flex items-start gap-3">
              <span
                className={cn(
                  "mt-1.5 size-2 shrink-0 rounded-full",
                  DOT[alert.severity],
                )}
              />
              <div className="min-w-0 flex-1">
                <p
                  title={alert.title}
                  className="text-[13px] font-semibold leading-snug text-slate-800 dark:text-slate-100"
                >
                  {alert.title}
                </p>
                {alert.detail && (
                  <p className="mt-0.5 truncate text-[11px] text-slate-400">
                    {alert.detail}
                  </p>
                )}
              </div>
              {alert.action && (
                <Button
                  size="sm"
                  variant={
                    alert.action.tone === "primary" ? "default" : "outline"
                  }
                  onClick={alert.action.onClick}
                  className="shrink-0 cursor-pointer rounded-lg"
                >
                  {alert.action.label}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Lo que nadie lleva dentro de las líneas del comercial, con "Tomarla". */
export function FreeInMyLines({
  items,
  onTake,
  onOpen,
  isTaking,
}: {
  items: ContractingItem[];
  onTake: (id: number) => void;
  onOpen: (id: number) => void;
  isTaking: boolean;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border bg-card p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-base font-bold">Libres en mis líneas</h3>
        <span className="text-xs text-slate-400">
          {items.length} oportunidad{items.length === 1 ? "" : "es"}
        </span>
      </div>

      {items.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">
          Todo lo de tus líneas ya tiene responsable.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.slice(0, 4).map((item) => (
            <li
              key={item.contracting_id}
              className="flex items-center gap-3 rounded-xl border p-3"
            >
              <button
                type="button"
                onClick={() => onOpen(item.contracting_id)}
                className="min-w-0 flex-1 cursor-pointer text-left"
              >
                <p className="flex items-center gap-1.5 truncate text-xs font-bold text-slate-500">
                  <span className="truncate">
                    {item.contracting_nomenclature}
                  </span>
                  {item.contracting_minorid && <MinorBadge />}
                </p>
                <p className="truncate text-sm font-medium">{label(item)}</p>
                <p className="truncate text-xs text-slate-400">
                  {item.contracting_entity} · {amountLabel(item) ?? "—"}
                </p>
              </button>
              <Button
                size="sm"
                disabled={isTaking}
                onClick={() => onTake(item.contracting_id)}
              >
                Tomarla
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-slate-400">
        Solo ves oportunidades sin responsable de las líneas a las que
        perteneces. Al tomarla, queda registrada a tu nombre en el historial.
      </p>
    </section>
  );
}

/** Fecha corta y relativa, como la usa el resto del tablero. */
export function shortDate(iso: string) {
  const date = dayjs(iso);
  return `${date.format("DD MMM")} · ${date.format("hh:mm a")}`;
}
